import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { useToast } from '../../../../hooks/useToast';
import { Tooltip } from '../../../common/Tooltip';
import styles from '../ScenarioGenerator.module.css';
import type { SceneItemProps } from '../SceneItem';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useGenerationState } from '../../state/useGenerationState';
import { MaterialMentionPopup } from '../MaterialMentionPopup';
import { MentionTokenChipOverlay } from '../MentionTokenChipOverlay';
import { MentionTokenInlineText } from '../MentionTokenInlineText';
import { CopyTextButton } from './CopyTextButton';
import { useMaterialMention, type SceneFrameInsertResult, type SceneFramePosition } from '../hooks/useMaterialMention';
import { buildSceneFrameMentionCandidates, readSceneFrameMentionRecords } from '../../state/mentionUtils';
import { createSceneFrameMention } from '../../../../features/scenarios/api';
import { useMentionTokenChips } from '../hooks/useMentionTokenChips';
import { useSceneFrameMentionThumbnails } from '../hooks/useSceneFrameMentionThumbnails';
import {
  applyCastRolesToMentionActorSources,
  buildDialogueSpeakerOptions,
  buildVoiceSpeakerExtraOptions,
  createEmptyVoiceLine,
  resolveDialogueLinesSaveResult,
  toVoiceScriptDialogueLines,
  type DialogueLine,
  type SpeakerLine,
} from '../dialogueLines';
import { useQueryClient } from '@tanstack/react-query';
import { scenarioVoiceMapQueryKey } from '../../../../features/scenarios/api';
import {
  MOTION_V2_DIALOGUE_LINES_MAX_COUNT,
  MOTION_V2_DIALOGUE_LINE_TEXT_MAX_CHARS,
  isMotionV2Script,
} from '../../../../features/scenarios/scriptContract';
import { DIALOGUE_LINE_LABEL_MAX_CHARS } from '../../../../features/scenarios/types';
import {
  MOTION_V2_VISUAL_GUIDE,
  MOTION_V2_VISUAL_PLACEHOLDER,
} from '../voiceLines';
import { useInferSceneTelopLines } from '../../../../features/scenarios/hooks';
import {
  TELOP_LINES_EDITOR_LABELS,
  TELOP_LINES_EDITOR_LIMITS,
  buildDialogueSpeakerExtraOptions,
  buildTelopSpeakerExtraOptions,
  createEmptyTelopEditorLine,
  editorLinesToTelopLines,
  resolveTelopLinesSaveResult,
  scenarioCharacters,
  telopLinesToEditorLines,
  type TelopLine,
} from '../telopLines';
import { DialogueLinesEditor } from './DialogueLinesEditor';
import { DialogueLinesView } from './DialogueLinesView';
import { TelopLinesView } from './TelopLinesView';
import { BrandCutMaterialThumb } from './BrandCutMaterialThumb';
import { patchSceneInLastResult } from './lastResultScenePatch';
import { deriveSceneInputFamily, listSceneFrameMentionSources, type SceneFrameMentionSourceRow } from '../utils';

type SceneDetailColumnProps = Pick<SceneItemProps, 'scene' | 'sceneKey' | 'scenario'> & {
  /** ブランドカットに動画素材が紐付いているか (SceneItem 側の useSceneTimeline で計算)。 */
  brandCutHasBoundVideo: boolean;
  /** 「既存の素材を挿入」で追加したブランドカットか (シーン詳細の編集欄を出さない)。 */
  brandCutFromMaterial: boolean;
};

/**
 * シーンカード右列の「シーン詳細」ブロック (映像・ビジュアル / セリフ / テロップ、
 * およびブランドカットの紐付け動画サムネイル)。
 */
export function SceneDetailColumn({
  scene,
  sceneKey: key,
  scenario,
  brandCutHasBoundVideo,
  brandCutFromMaterial,
}: SceneDetailColumnProps) {
  const {
    videoGenerationMode,
    editingField,
    editingValue,
    setEditingField,
    setEditingValue,
    handleSaveField,
    updateSceneMutation,
    lastResult,
    setLastResult,
    mentionEligibleAssets,
    mentionActorSources: rawMentionActorSources,
    editingDialogueLinesRef,
    editingTelopLinesRef,
  } = useSceneHandlers();
  const toast = useToast();
  // 俳優候補に役柄名 (generation_metadata.cast_roles) を付ける。映像欄の @チップのホバー名・
  // 候補ポップアップ・話者チップの名前が俳優の表示名ではなく役柄名になる
  // (docs/features/scenario-cast-role-names-plan.md)。
  const mentionActorSources = useMemo(
    () => applyCastRolesToMentionActorSources(rawMentionActorSources, scenario),
    [rawMentionActorSources, scenario],
  );

  // 顔あり/顔なしバッジの表示条件: フレームモード × Seedance × 写実系 のときだけ表示
  // (この構成でのみ has_visible_face が生成方式 = イラスト化/r2v 切替に影響するため §3.5)。
  // 値自体はモデル・スタイルを切り替えても DB に保持される。ブランドカットは対象外。
  const { videoSettings, visualStyleIsStylized, avoidFaces } = useGenerationState();
  // シーンの入力系統 (P2 step 3。videoGenerationMode(シナリオ単位) の代わりにシーン単位で
  // 判定する。docs/features/scene-location-and-production-mode-plan.md §3.1)。
  const sceneFamily = deriveSceneInputFamily(scenario, scene);
  const showFaceBadge = (
    sceneFamily === 'frame'
    && !!(videoSettings.videoModel as string | undefined)?.startsWith('seedance')
    && !visualStyleIsStylized
    && !avoidFaces
    && !scene.brand_cut_position
  );

  // 素材 @メンション (docs/features/material-at-mention-plan.md P3)。
  // 「映像・ビジュアル」のインライン編集 textarea (scenes.visual) に接続する。
  // editingField/editingValue は SceneHandlersContext 経由の共有 state (全シーン共通・
  // 一度に1フィールドしか編集されない) だが、フックはシーンごとの SceneDetailColumn
  // インスタンス単位で持つため、実際に textarea が描画されているインスタンス以外は
  // 検出処理が一切走らず無害。
  // 候補は mentionEligibleAssets (selectedAssets のうち動画生成の参照上限で実際に
  // 送信される素材。プロンプトモード基準の近似。レビュー指摘 P1) + mentionActorSources
  // (選択中の AI俳優。常に候補。「俳優メンション」節) を使う。
  //
  // 他シーンの動画フレーム (docs/features/scene-video-frame-materials-visibility-plan.md §2.2):
  // 記録 (generation_metadata.scene_frame_mentions)から再構成した候補をチップ・バッジに合流し、
  // ポップアップには「他シーンの動画」節 (行の[最初] [最後]で挿入APIを呼ぶ)を足す。
  // サムネイル・「参照先なし」はGET .../frame-mentions (フレーム素材自身のURL)から得る。
  // シーンの入力フレームでは代用しない (参照している素材と別の画像になり得るため)。
  const sceneFrameRecords = useMemo(
    () => readSceneFrameMentionRecords(scenario.generation_metadata),
    [scenario.generation_metadata],
  );
  const {
    thumbnails: sceneFrameThumbnails,
    missingFrameIds: sceneFrameMissingIds,
    applyInsertResult: applySceneFrameInsertResult,
  } = useSceneFrameMentionThumbnails(scenario.id, sceneFrameRecords);
  const sceneFrameCandidates = useMemo(
    () => buildSceneFrameMentionCandidates(
      sceneFrameRecords,
      scenario.scenes || [],
      { thumbnails: sceneFrameThumbnails, missingFrameIds: sceneFrameMissingIds },
    ),
    [sceneFrameRecords, scenario.scenes, sceneFrameThumbnails, sceneFrameMissingIds],
  );
  const sceneFrameSources = useMemo(
    () => listSceneFrameMentionSources(scenario, scene.id),
    [scenario, scene.id],
  );
  const handleInsertSceneFrame = useCallback(async (
    row: SceneFrameMentionSourceRow,
    position: SceneFramePosition,
  ): Promise<SceneFrameInsertResult | null> => {
    try {
      const result = await createSceneFrameMention(scenario.id, row.sceneId, position);
      applySceneFrameInsertResult(result);
      // 記録はマージ後のmap全体が返るので、lastResultのシナリオのgeneration_metadataを置き換える
      // (SceneLocationControlのlocationsと同じくlastResultを直接更新する既存パターン)。
      setLastResult((prev) => (prev ? {
        ...prev,
        scenarios: prev.scenarios.map((s: any) => (s.id === scenario.id ? {
          ...s,
          generation_metadata: { ...(s.generation_metadata || {}), scene_frame_mentions: result.scene_frame_mentions },
        } : s)),
      } : prev));
      return { frameMaterialId: result.frame_material_id, id4: result.id4 };
    } catch (error) {
      const data = (error as AxiosError<{ message?: string; error?: string }> | undefined)?.response?.data;
      toast.error(data?.message || data?.error || 'フレームの取得に失敗しました');
      return null;
    }
  }, [scenario.id, setLastResult, applySceneFrameInsertResult, toast]);

  // 映像欄の編集セッション識別子 (編集開始ごとに増える・編集していない間はnull)。
  // [最初]/[最後]の確保待ちの間にキャンセル・保存・別シーン/別欄へ切り替わったら、
  // 完了時に共有のeditingValue (別の入力欄の本文)へ挿入しないための照合に使う。
  const isEditingVisual = editingField?.sceneId === scene.id && editingField?.field === 'visual';
  const visualEditSessionRef = useRef({ active: false, seq: 0 });
  if (visualEditSessionRef.current.active !== isEditingVisual) {
    // 描画中の更新だが、同じ入力に対して冪等 (StrictModeの二重描画でも1回だけ増える)
    visualEditSessionRef.current = {
      active: isEditingVisual,
      seq: visualEditSessionRef.current.seq + (isEditingVisual ? 1 : 0),
    };
  }
  const visualEditSessionKey = isEditingVisual ? `${scene.id}:${visualEditSessionRef.current.seq}` : null;

  const visualTextareaRef = useRef<HTMLTextAreaElement>(null);
  const visualMentionPopupRef = useRef<HTMLDivElement>(null);
  const visualMention = useMaterialMention({
    textareaRef: visualTextareaRef,
    value: editingValue,
    onChange: setEditingValue,
    assets: mentionEligibleAssets,
    actors: mentionActorSources,
    popupRef: visualMentionPopupRef,
    sceneFrameCandidates,
    sceneFrameSources,
    onInsertSceneFrame: scene.id ? handleInsertSceneFrame : undefined,
    editSessionKey: visualEditSessionKey,
  });
  // トークンのチップ表示 (オーバーレイ装飾方式・docs/features/material-at-mention-plan.md
  // 「トークンのチップ表示」節)。本文は @A#3f9c のまま変えず、見た目だけ重ねる。
  const visualMentionChips = useMentionTokenChips({
    textareaRef: visualTextareaRef,
    value: editingValue,
    assets: mentionEligibleAssets,
    actors: mentionActorSources,
    sceneFrameCandidates,
  });

  // 発話単位話者帰属 (dialogue_lines)。NULL=レガシー(フラット dialogue) /
  // 配列([] 含む)=構造化モード。docs/features/scenario-dialogue-speaker-plan.md §3.7/§3.8。
  // 声の台本 (script_contract='motion-v2'・§10.7.2): セリフ欄は「声」(話者付き・ナレーター行を含む) になり、
  // テロップは動画エディタで重ねる (voice_ref は UI で扱わない)。旧契約のモーション台本・実写は従来どおり
  const voiceScript = isMotionV2Script(scenario) && !scene.brand_cut_position;
  const queryClient = useQueryClient();
  const isStructuredDialogue = scene.dialogue_lines != null || voiceScript;
  const isEditingDialogueLines = editingField?.sceneId === scene.id && editingField?.field === 'dialogue' && isStructuredDialogue;
  const [editingDialogueLines, setEditingDialogueLines] = useState<DialogueLine[]>([]);
  const [savingDialogueLines, setSavingDialogueLines] = useState(false);
  // 外側クリックの破棄確認 (useSceneActions) 用に、編集中の最新値を共有 ref へミラーする
  // (SceneHandlersContext.editingDialogueLinesRef)。このシーンが実際に編集中の間だけ
  // 書き込む — editingField はシーンをまたぐ単一の共有 state なので、他シーンの
  // SceneDetailColumn インスタンスは isEditingDialogueLines=false のままここに来ず
  // 上書きしない (2026-08-17 要件2)。
  useEffect(() => {
    if (isEditingDialogueLines) {
      editingDialogueLinesRef.current = editingDialogueLines;
    }
  }, [isEditingDialogueLines, editingDialogueLines, editingDialogueLinesRef]);
  // 話者ピッカーの選択肢: シナリオのキャスト俳優 (casting_model_ids と
  // seedance_preset_actor_ids の両方。§3.8)。@メンション候補 (mentionActorSources) とは
  // 独立: モデル種別/スタイライズ/参照上限によるゲートはここでは適用しない。
  const dialogueSpeakerOptions = useMemo(() => buildDialogueSpeakerOptions(scenario), [scenario]);

  // テロップ行ごとの話者割り当て (telop_lines)。NULL=レガシー(フラット telop の textarea) /
  // 配列([] 含む)=構造化モード (行エディタ)。docs/features/scenario-telop-speaker-plan.md §3.7。
  const isStructuredTelop = scene.telop_lines != null;
  const isEditingTelopLines = editingField?.sceneId === scene.id && editingField?.field === 'telop' && isStructuredTelop;
  const [editingTelopLines, setEditingTelopLines] = useState<SpeakerLine[]>([]);
  const [savingTelopLines, setSavingTelopLines] = useState(false);
  useEffect(() => {
    if (isEditingTelopLines) {
      editingTelopLinesRef.current = editingTelopLines;
    }
  }, [isEditingTelopLines, editingTelopLines, editingTelopLinesRef]);
  // 話者ピッカーの追加候補: 登場人物宣言 (generation_metadata.characters) + ナレーション + 話者なし。
  const telopSpeakerExtraOptions = useMemo(
    () => buildTelopSpeakerExtraOptions(scenarioCharacters(scenario)),
    [scenario],
  );
  // セリフの話者ピッカーにも登場人物宣言を出す (character_key 付きで保存される)。
  const dialogueSpeakerExtraOptions = useMemo(
    () => (voiceScript
      ? buildVoiceSpeakerExtraOptions(scenarioCharacters(scenario))
      : buildDialogueSpeakerExtraOptions(scenarioCharacters(scenario))),
    [scenario, voiceScript],
  );
  const startTelopEditing = (lines: TelopLine[]) => {
    setEditingTelopLines(telopLinesToEditorLines(lines));
  };

  // レガシーシーン (telop_lines NULL) の昇格: フラット telop から話者を推定した telop_lines を
  // BE が保存し、その場で行エディタを開く (docs/features/scenario-telop-speaker-plan.md「シーン単位の昇格」)。
  const inferTelopLinesMutation = useInferSceneTelopLines();
  const handleInferTelopLines = async () => {
    if (!scene.id) {
      toast.error('シーンを先に保存してください');
      return;
    }
    try {
      const row = await inferTelopLinesMutation.mutateAsync({ sceneId: scene.id });
      const nextLines = (row as any)?.telop_lines ?? [];
      const nextTelop = (row as any)?.telop ?? scene.telop ?? '';
      if (lastResult) {
        setLastResult((prev) => (prev ? {
          ...prev,
          scenarios: prev.scenarios.map((s: any) => ({
            ...s,
            scenes: s.scenes.map((sc: any) => (sc.id === scene.id ? { ...sc, telop_lines: nextLines, telop: nextTelop } : sc)),
          })),
        } : prev));
      }
      startTelopEditing(nextLines);
      setEditingField({ sceneId: scene.id, field: 'telop' });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'テロップの話者割り当てに失敗しました');
    }
  };

  const handleSaveTelopLines = async () => {
    if (!scene.id) {
      toast.error('シーンを先に保存してください');
      return;
    }
    const sentLines = editorLinesToTelopLines(editingTelopLines);
    setSavingTelopLines(true);
    try {
      const updatedScene = await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: { telop_lines: sentLines },
      });
      if (lastResult) {
        const { telop_lines: nextTelopLines, telop: nextTelop } =
          resolveTelopLinesSaveResult(scene.id, sentLines, updatedScene);
        setLastResult((prev) => (prev ? {
          ...prev,
          scenarios: prev.scenarios.map((s: any) => ({
            ...s,
            scenes: s.scenes.map((sc: any) =>
              sc.id === scene.id ? { ...sc, telop_lines: nextTelopLines, telop: nextTelop } : sc
            ),
          })),
        } : prev));
      }
      toast.success('テロップを更新しました');
      setEditingField(null);
    } catch (error: any) {
      const message = error?.response?.data?.error || 'テロップの更新に失敗しました';
      toast.error(message);
    } finally {
      setSavingTelopLines(false);
    }
  };

  const handleSaveDialogueLines = async () => {
    if (!scene.id) {
      toast.error('シーンを先に保存してください');
      return;
    }
    setSavingDialogueLines(true);
    // 声の台本: ナレーター行の形を揃え、新しい行に line_id を付けて送る (既存行の id は保つ)
    const sentDialogueLines = voiceScript ? toVoiceScriptDialogueLines(editingDialogueLines) : editingDialogueLines;
    try {
      const updatedScene = await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: { dialogue_lines: sentDialogueLines },
      });
      // 声の台本: 声の行を消すと BE がその行を指していたテロップの voice_ref (データのみ・UI では扱わない) を外すので、
      // 応答の telop_lines を取り込む
      const nextVoiceTelop = voiceScript && updatedScene && (updatedScene as { id?: string }).id === scene.id
        ? ((updatedScene as { telop_lines?: TelopLine[] | null }).telop_lines ?? undefined)
        : undefined;
      // 話者の一覧とナレーションの鮮度 (voice-map) が変わる (実写でも声だけの登場人物の行はナレーションになる)
      void queryClient.invalidateQueries({ queryKey: scenarioVoiceMapQueryKey(scenario.id) });
      if (lastResult) {
        // PUT /generation/scenes/<id> (backend/routes/generation/scenario_crud.py
        // update_scene) は更新後のシーン行をそのまま返す。BE が strict 正規化・
        // actor_id の組織スコープ検証 (無効なものは null 化)・dialogue/script の
        // 3値導出 (docs/features/scenario-dialogue-speaker-plan.md §3.6) を行った
        // 結果を反映する (レビューP1指摘)。
        const { dialogue_lines: nextDialogueLines, dialogue: nextDialogue, script: nextScript } =
          resolveDialogueLinesSaveResult(scene.id, sentDialogueLines, updatedScene);
        setLastResult((prev) => (prev ? {
          ...prev,
          scenarios: prev.scenarios.map((s: any) => ({
            ...s,
            scenes: s.scenes.map((sc: any) =>
              sc.id === scene.id
                ? {
                    ...sc,
                    dialogue_lines: nextDialogueLines,
                    ...(nextVoiceTelop !== undefined ? { telop_lines: nextVoiceTelop } : {}),
                    ...(nextDialogue !== undefined ? { dialogue: nextDialogue } : {}),
                    ...(nextScript !== undefined ? { script: nextScript } : {}),
                  }
                : sc
            ),
          })),
        } : prev));
      }
      toast.success(voiceScript ? 'ナレーションを更新しました' : 'セリフを更新しました');
      setEditingField(null);
    } catch (error: any) {
      // strict 正規化違反は 422 + { error, violation } (docs/features/
      // scenario-dialogue-speaker-plan.md §3.4/§3.6)。黙って切り捨てず編集者へ返す。
      const message = error?.response?.data?.error || (voiceScript ? 'ナレーションの更新に失敗しました' : 'セリフの更新に失敗しました');
      toast.error(message);
    } finally {
      setSavingDialogueLines(false);
    }
  };

  return (
    <>
                              {/* シーン詳細 */}
                              <div className={styles.sceneDetails}>
                                <div style={{ flex: 1 }}>
                                {/* 素材から追加したブランドカット(タイトル/エンドカード)は素材がそのまま
                                    クリップになるため シーン/セリフ/テロップ は意味を持たない。これらを除去し、
                                    ストーリーボードでは紐付け動画のサムネイルを表示する (クリックで素材モーダルが
                                    開いて再生)。それ以外のモードではサムネイルを左のフレーム列に出すので何も出さない。
                                    ストーリーボードは従来どおり動画が紐付いているブランドカットが対象。 */}
                                {videoGenerationMode === 'storyboard' && brandCutHasBoundVideo ? (
                                  <BrandCutMaterialThumb scene={scene} sceneKey={key} size="compact" />
                                ) : videoGenerationMode !== 'storyboard' && brandCutFromMaterial ? null : (
                                <>
                                {/* 映像・ビジュアル */}
                                <div className={styles.sceneFieldRow}>
                                  {editingField?.sceneId === scene.id && editingField?.field === 'visual' ? (
                                    <div className={styles.inlineEditContainer} data-scene-inline-edit>
                                      <img src="/movies_reel.svg" alt="映像" className={styles.sceneIcon} />
                                      {/* トークンのチップ表示のオーバーレイを重ねるため、textarea だけを
                                          position:relative のラッパーで包む (アイコン/保存・キャンセル
                                          ボタンはこれまで通り inlineEditContainer の直接の flex item)。
                                          textarea 自体は width:100% でラッパーいっぱいに広げ、
                                          flex:1 はラッパー側へ移す (レイアウトを崩さないため)。 */}
                                      <div className={styles.mentionTextareaWrapper}>
                                        <textarea
                                          ref={visualTextareaRef}
                                          className={styles.inlineEditTextarea}
                                          style={{ flex: 'none', width: '100%' }}
                                          value={editingValue}
                                          onChange={visualMention.textareaHandlers.onChange}
                                          onSelect={visualMention.textareaHandlers.onSelect}
                                          onCompositionStart={visualMention.textareaHandlers.onCompositionStart}
                                          onCompositionEnd={visualMention.textareaHandlers.onCompositionEnd}
                                          onKeyDown={(e) => {
                                            // @メンションポップアップ表示中は、こちらの
                                            // Escape/Enter (キャンセル/保存) より優先させる
                                            // (useMaterialMention が isOpen 時のみ preventDefault する)。
                                            visualMention.textareaHandlers.onKeyDown(e);
                                            if (e.defaultPrevented) return;
                                            if (e.key === 'Escape') {
                                              e.preventDefault();
                                              setEditingField(null);
                                              setEditingValue('');
                                            } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                              e.preventDefault();
                                              handleSaveField(scene.id, 'visual', editingValue);
                                            }
                                          }}
                                          autoFocus
                                          rows={Math.max(voiceScript ? 6 : 5, editingValue.split('\n').length)}
                                          placeholder={voiceScript ? MOTION_V2_VISUAL_PLACEHOLDER : undefined}
                                          aria-label={voiceScript ? '画面設計' : undefined}
                                        />
                                        <MentionTokenChipOverlay chips={visualMentionChips.chips} />
                                        {voiceScript && (
                                          <span
                                            data-testid="motion-v2-visual-guide"
                                            style={{
                                              display: 'block', marginTop: 4, fontSize: 11,
                                              color: editingValue.length > MOTION_V2_VISUAL_GUIDE.max
                                                ? 'rgba(245, 200, 120, 0.85)' : 'rgba(245, 241, 230, 0.45)',
                                            }}
                                          >
                                            {`画面設計: 画面 / 動き / 強調 の 3 ブロックで書きます・目安 ${MOTION_V2_VISUAL_GUIDE.min}〜${MOTION_V2_VISUAL_GUIDE.max} 字 (現在 ${editingValue.length} 字)`}
                                          </span>
                                        )}
                                      </div>
                                      {visualMention.isOpen && (
                                        <MaterialMentionPopup
                                          ref={visualMentionPopupRef}
                                          candidates={visualMention.candidates}
                                          activeIndex={visualMention.activeIndex}
                                          position={visualMention.popupPosition}
                                          onSelect={visualMention.selectCandidate}
                                          onHoverIndex={visualMention.setActiveIndex}
                                          sceneFrameRows={visualMention.sceneFrameRows}
                                          sceneFramePosition={visualMention.sceneFramePosition}
                                          pendingSceneFrame={visualMention.pendingSceneFrame}
                                          onSelectSceneFrame={visualMention.selectSceneFrame}
                                        />
                                      )}
                                      <div className={styles.inlineEditActions}>
                                        <button
                                          type="button"
                                          className={styles.inlineEditCancel}
                                          onClick={() => {
                                            setEditingField(null);
                                            setEditingValue('');
                                          }}
                                          title="キャンセル (Esc)"
                                        >
                                          キャンセル
                                        </button>
                                        <button
                                          type="button"
                                          className={styles.inlineEditSave}
                                          onClick={() => handleSaveField(scene.id, 'visual', editingValue)}
                                          title="保存"
                                        >
                                          保存
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={styles.sceneVisual}>
                                      <Tooltip content="映像・ビジュアル"><img src="/movies_reel.svg" alt="映像" className={styles.sceneIcon} /></Tooltip>
                                      <span>
                                        {/* 場面継続/転換のドロップダウンは、フレーム/参照画像
                                            モードともフレームカード・サムネイル領域の上部
                                            (SceneFrameCarousel) へ移設した (二重表示の解消。
                                            正本は同じ continues_previous_shot)。
                                            ストーリーボードモードでは元々意味を持たないため
                                            非表示のまま。 */}
                                        {/* 顔あり/顔なし: LLM 判定の手動訂正。faceSafe (イラスト絵コンテ生成 →
                                            r2v 実写化) と通常 i2v の切替に使う。欠損は顔あり扱い (安全側)。 */}
                                        {showFaceBadge && (
                                          <Tooltip
                                            content={(scene.has_visible_face ?? true)
                                              ? '人物の顔を含むシーンとして扱います。Seedance では、最初のフレームをイラスト絵コンテとして生成します。'
                                              : '人物の顔を含まないシーンとして扱います。生成した最初のフレームを動画の開始画像として使用します。'}
                                          >
                                            <select
                                              className={`badge ${(scene.has_visible_face ?? true) ? 'badge-orange' : 'badge-gold'}`}
                                              style={{ marginRight: 6 }}
                                              value={(scene.has_visible_face ?? true) ? 'face' : 'no-face'}
                                              onClick={(e) => e.stopPropagation()}
                                              onChange={async (e) => {
                                                const newValue = e.target.value === 'face';
                                                // 楽観更新: 選んだ瞬間に画面の正本へ当て、保存に失敗したときだけ元の値へ戻す。
                                                // 応答を待ってから当てると、controlled な select が応答までの間に
                                                // 古い値へ引き戻されて「元に戻された」ように見える (本番の往復では
                                                // 1 秒前後)。欠損 (旧シナリオ) の元値は undefined のまま戻す。
                                                const previousValue = scene.has_visible_face;
                                                setLastResult((prev) => patchSceneInLastResult(prev, scene.id, { has_visible_face: newValue }));
                                                try {
                                                  await updateSceneMutation.mutateAsync({
                                                    sceneId: scene.id,
                                                    payload: { has_visible_face: newValue },
                                                  });
                                                } catch {
                                                  setLastResult((prev) => patchSceneInLastResult(prev, scene.id, { has_visible_face: previousValue }));
                                                  toast.error('更新に失敗しました');
                                                }
                                              }}
                                            >
                                              <option value="face">顔あり</option>
                                              <option value="no-face">顔なし</option>
                                            </select>
                                          </Tooltip>
                                        )}
                                        {/* 読み取り表示 (編集モード外) でも @メンショントークンを
                                            チップで見せる (トークンのチップ表示・インライン置換方式。
                                            docs/features/material-at-mention-plan.md)。編集中の
                                            オーバーレイと違い、こちらは通常の DOM なので座標計測不要。 */}
                                        {scene.visual ? (
                                          <MentionTokenInlineText
                                            text={scene.visual}
                                            assets={mentionEligibleAssets}
                                            actors={mentionActorSources}
                                            sceneFrameCandidates={sceneFrameCandidates}
                                          />
                                        ) : (
                                          '（未設定）'
                                        )}
                                      </span>
                                      <button
                                        type="button"
                                        className={styles.fieldEditButton}
                                        onClick={() => {
                                          setEditingField({ sceneId: scene.id, field: 'visual' });
                                          setEditingValue(scene.visual || '');
                                        }}
                                        title="映像・ビジュアルを編集"
                                      >
                                        <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* セリフ */}
                                <div className={styles.sceneFieldRow}>
                                  {isStructuredDialogue ? (
                                    isEditingDialogueLines ? (
                                      <div
                                        className={styles.inlineEditContainer}
                                        data-scene-inline-edit
                                        onKeyDown={(e) => {
                                          if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setEditingField(null);
                                          }
                                        }}
                                      >
                                        <img src={voiceScript ? '/microphone_1.svg' : '/messages_people_person_bubble_circle_1.svg'} alt={voiceScript ? 'ナレーション' : 'セリフ'} className={styles.sceneIcon} />
                                        {/* 保存/キャンセルは「+ 行を追加」と同じ footer 行の右端に同居させる
                                            (actionsSlot・2026-08-17 要件5)。他フィールドのようなフォーム下・
                                            独立行の inlineEditActions は使わない。 */}
                                        <DialogueLinesEditor
                                          lines={editingDialogueLines}
                                          onChange={setEditingDialogueLines}
                                          onRequestSave={handleSaveDialogueLines}
                                          speakerOptions={dialogueSpeakerOptions}
                                          extraOptions={dialogueSpeakerExtraOptions}
                                          {...(voiceScript ? {
                                            limits: {
                                              maxCount: MOTION_V2_DIALOGUE_LINES_MAX_COUNT,
                                              textMaxChars: MOTION_V2_DIALOGUE_LINE_TEXT_MAX_CHARS,
                                              labelMaxChars: DIALOGUE_LINE_LABEL_MAX_CHARS,
                                            },
                                            labels: { textPlaceholder: 'ナレーション (1 行 = 1 息・60 字以内)', emptyHint: '（ナレーションなし）' },
                                            createEmptyLine: () => createEmptyVoiceLine(editingDialogueLines),
                                          } : {})}
                                          actors={mentionActorSources}
                                          actionsSlot={
                                            <div style={{ display: 'flex', gap: 8 }}>
                                              <button
                                                type="button"
                                                className={styles.inlineEditCancel}
                                                onClick={() => setEditingField(null)}
                                                title="キャンセル (Esc)"
                                              >
                                                キャンセル
                                              </button>
                                              <button
                                                type="button"
                                                className={styles.inlineEditSave}
                                                onClick={handleSaveDialogueLines}
                                                disabled={savingDialogueLines}
                                                title="保存"
                                              >
                                                保存
                                              </button>
                                            </div>
                                          }
                                        />
                                      </div>
                                    ) : (
                                      <div className={styles.sceneScript}>
                                        {voiceScript ? (
                                          <Tooltip content="ナレーション（話者付きで順に読む。ナレーターも話者の 1 人）"><img src="/microphone_1.svg" alt="ナレーション" className={styles.sceneIcon} /></Tooltip>
                                        ) : (
                                          <Tooltip content="セリフ（動画内で登場人物が話す言葉）"><img src="/messages_people_person_bubble_circle_1.svg" alt="セリフ" className={styles.sceneIcon} /></Tooltip>
                                        )}
                                        <DialogueLinesView lines={scene.dialogue_lines || []} actors={mentionActorSources} />
                                        <button
                                          type="button"
                                          className={styles.fieldEditButton}
                                          onClick={() => {
                                            setEditingField({ sceneId: scene.id, field: 'dialogue' });
                                            setEditingDialogueLines(scene.dialogue_lines ?? []);
                                          }}
                                          title={voiceScript ? 'ナレーションを編集' : 'セリフを編集'}
                                        >
                                          <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
                                        </button>
                                        <CopyTextButton
                                          text={(scene.dialogue_lines || []).map((l) => l.text).join('\n')}
                                          label={voiceScript ? 'ナレーション' : 'セリフ'}
                                        />
                                      </div>
                                    )
                                  ) : editingField?.sceneId === scene.id && editingField?.field === 'dialogue' ? (
                                    <div className={styles.inlineEditContainer} data-scene-inline-edit>
                                      <img src="/messages_people_person_bubble_circle_1.svg" alt="セリフ" className={styles.sceneIcon} />
                                      <textarea
                                        className={styles.inlineEditTextarea}
                                        value={editingValue}
                                        onChange={(e) => setEditingValue(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setEditingField(null);
                                            setEditingValue('');
                                          } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                            e.preventDefault();
                                            handleSaveField(scene.id, 'dialogue', editingValue);
                                          }
                                        }}
                                        autoFocus
                                        rows={Math.max(1, editingValue.split('\n').length)}
                                      />
                                      <div className={styles.inlineEditActions}>
                                        <button
                                          type="button"
                                          className={styles.inlineEditCancel}
                                          onClick={() => {
                                            setEditingField(null);
                                            setEditingValue('');
                                          }}
                                          title="キャンセル (Esc)"
                                        >
                                          キャンセル
                                        </button>
                                        <button
                                          type="button"
                                          className={styles.inlineEditSave}
                                          onClick={() => handleSaveField(scene.id, 'dialogue', editingValue)}
                                          title="保存"
                                        >
                                          保存
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={`${styles.sceneScript} ${!scene.dialogue ? styles.sceneFieldEmpty : ''}`}>
                                      <Tooltip content="セリフ（動画内で登場人物が話す言葉）"><img src="/messages_people_person_bubble_circle_1.svg" alt="セリフ" className={styles.sceneIcon} /></Tooltip>
                                      <span>{scene.dialogue || '（セリフなし）'}</span>
                                      <button
                                        type="button"
                                        className={styles.fieldEditButton}
                                        onClick={() => {
                                          setEditingField({ sceneId: scene.id, field: 'dialogue' });
                                          setEditingValue(scene.dialogue || '');
                                        }}
                                        title="セリフを編集"
                                      >
                                        <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
                                      </button>
                                      <CopyTextButton text={scene.dialogue || ''} label="セリフ" />
                                    </div>
                                  )}
                                </div>

                                {/* テロップ */}
                                <div className={styles.sceneFieldRow}>
                                  {isStructuredTelop ? (
                                    isEditingTelopLines ? (
                                      <div
                                        className={styles.inlineEditContainer}
                                        data-scene-inline-edit
                                        onKeyDown={(e) => {
                                          if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setEditingField(null);
                                          }
                                        }}
                                      >
                                        <img src="/subtitles.svg" alt="テロップ" className={styles.sceneIcon} />
                                        <DialogueLinesEditor
                                          lines={editingTelopLines}
                                          onChange={setEditingTelopLines}
                                          onRequestSave={handleSaveTelopLines}
                                          speakerOptions={dialogueSpeakerOptions}
                                          extraOptions={telopSpeakerExtraOptions}
                                          limits={TELOP_LINES_EDITOR_LIMITS}
                                          labels={TELOP_LINES_EDITOR_LABELS}
                                          allowLineBreaks
                                          createEmptyLine={createEmptyTelopEditorLine}
                                          actors={mentionActorSources}
                                          actionsSlot={
                                            <div style={{ display: 'flex', gap: 8 }}>
                                              <button
                                                type="button"
                                                className={styles.inlineEditCancel}
                                                onClick={() => setEditingField(null)}
                                                title="キャンセル (Esc)"
                                              >
                                                キャンセル
                                              </button>
                                              <button
                                                type="button"
                                                className={styles.inlineEditSave}
                                                onClick={handleSaveTelopLines}
                                                disabled={savingTelopLines}
                                                title="保存"
                                              >
                                                保存
                                              </button>
                                            </div>
                                          }
                                        />
                                      </div>
                                    ) : (
                                      <div className={styles.sceneScript}>
                                        <Tooltip content="テロップ（画面上に表示するテキスト）"><img src="/subtitles.svg" alt="テロップ" className={styles.sceneIcon} /></Tooltip>
                                        <TelopLinesView
                                          lines={scene.telop_lines || []}
                                          actors={mentionActorSources}
                                        />
                                        <button
                                          type="button"
                                          className={styles.fieldEditButton}
                                          onClick={() => {
                                            setEditingField({ sceneId: scene.id, field: 'telop' });
                                            startTelopEditing(scene.telop_lines ?? []);
                                          }}
                                          title="テロップを編集"
                                        >
                                          <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
                                        </button>
                                        <CopyTextButton text={(scene.telop_lines || []).map((l) => l.text).join('\n')} label="テロップ" />
                                      </div>
                                    )
                                  ) : editingField?.sceneId === scene.id && editingField?.field === 'telop' ? (
                                    <div className={styles.inlineEditContainer} data-scene-inline-edit>
                                      <img src="/subtitles.svg" alt="テロップ" className={styles.sceneIcon} />
                                      <textarea
                                        className={styles.inlineEditTextarea}
                                        value={editingValue}
                                        onChange={(e) => setEditingValue(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setEditingField(null);
                                            setEditingValue('');
                                          } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                            e.preventDefault();
                                            handleSaveField(scene.id, 'telop', editingValue);
                                          }
                                        }}
                                        autoFocus
                                        rows={Math.max(1, editingValue.split('\n').length)}
                                      />
                                      <div className={styles.inlineEditActions}>
                                        <button
                                          type="button"
                                          className={styles.inlineEditCancel}
                                          onClick={() => {
                                            setEditingField(null);
                                            setEditingValue('');
                                          }}
                                          title="キャンセル (Esc)"
                                        >
                                          キャンセル
                                        </button>
                                        <button
                                          type="button"
                                          className={styles.inlineEditSave}
                                          onClick={() => handleSaveField(scene.id, 'telop', editingValue)}
                                          title="保存"
                                        >
                                          保存
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={`${styles.sceneScript} ${!scene.telop ? styles.sceneFieldEmpty : ''}`}>
                                      <Tooltip content="テロップ（画面上に表示するテキスト）"><img src="/subtitles.svg" alt="テロップ" className={styles.sceneIcon} /></Tooltip>
                                      <span>{scene.telop || '（テロップなし）'}</span>
                                      <button
                                        type="button"
                                        className={styles.fieldEditButton}
                                        onClick={() => {
                                          setEditingField({ sceneId: scene.id, field: 'telop' });
                                          setEditingValue(scene.telop || '');
                                        }}
                                        title="テロップを編集"
                                      >
                                        <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
                                      </button>
                                      <CopyTextButton text={scene.telop || ''} label="テロップ" />
                                      {/* 旧シナリオ (telop_lines 未設定) の昇格。テロップが空でも押せる
                                          (空の構造化モードになり、行エディタで追加できる)。 */}
                                      <Tooltip content="テロップの行ごとに話者を割り当てる (推定して編集できます)">
                                        <button
                                          type="button"
                                          className={styles.fieldEditButton}
                                          onClick={() => void handleInferTelopLines()}
                                          disabled={inferTelopLinesMutation.isPending}
                                          aria-label="話者を割り当てる"
                                          data-testid="telop-infer-speakers"
                                        >
                                          <img src="/messages_people_person_bubble_circle_1.svg" alt="" className={styles.fieldEditIcon} />
                                        </button>
                                      </Tooltip>
                                    </div>
                                  )}
                                </div>
                                </>
                                )}

                              </div>
                              </div>
    </>
  );
}
